import { ReactNode, useEffect, useRef, useState, VFC } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import {
  createMessageGroupMemo,
  deleteMessageGroupMemo,
  toMessageGroupMemo,
  useMessageGroupMemos,
} from 'src/api/messageGroupMemo'
import {
  createMessageMemo,
  deleteMessageMemo,
  toMessageMemo,
  useMessageMemos,
} from 'src/api/messageMemo'
import { useLoginUser } from 'src/api/user'
import { ProfileIcon, TrashIcon } from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { PaidUserLabel } from 'src/components/ProfilePreview/styles'
import { FeedbackScore } from 'src/components/Rating/FeedbackScore'
import { TooltipWrapper } from 'src/components/Tooltip'
import { SmallButton } from 'src/elements/Button'
import { MessageMemo } from 'src/models/MessageMemo'
import { Counter } from 'src/models/Room'
import { isPaidForMemo, LoginUser } from 'src/models/User'
import { shouldDisplayPaidPlanLabel } from 'src/models/User/role'
import {
  _filterbg,
  _menuicon,
  _messagebuttonbg,
  _messageformborder,
  _messagesbg,
  _mymessagebg,
  _profilegreen,
  truewhite,
} from 'src/styles/colors'
import {
  fontWeightBold,
  fontWeightLight,
  fontWeightMedium,
  fontWeightSemiBold,
} from 'src/styles/font'
import { flexCenter } from 'src/styles/mixins'
import { IS_DEVELOPMENT } from 'src/utils/env'
import styled, { css } from 'styled-components'
import { useMessageContext } from '../MessageContext'
import { DeleteMemoModal } from '../Modals/DeleteMemoModal'
import { FeedbackTab } from './FeedbackTab'
import { TabList, TabType } from './TabList'

export { SubMenuStyle as SubMenu }
export type SubMenuProps = {
  user: LoginUser
  showProfile: (userId: number) => void
  isSubMenuOpen: boolean
  activeTab: TabType
  setTabType: (tabType: TabType) => void
  toggleSubMenu: VoidFunction
}
const SubMenuComponent: VFC<SubMenuProps & { className?: string }> = ({
  user,
  showProfile,
  isSubMenuOpen,
  className,
  activeTab,
  setTabType,
  toggleSubMenu,
}) => {
  const textareaEl = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState('')
  const [deleteMemoModal, setDeleteMemoModal] = useState<
    MessageMemo | undefined
  >(undefined)

  const { room, mutate } = useMessageContext()

  const counters = room?.counters

  const [currentMemos, setCurrentMemos] = useState<MessageMemo[]>([])

  const paid = isPaidForMemo(user.plan.role)

  const { data: memos } = useMessageMemos(
    paid && room?.id && room.type === 'CompanyMessageRoom'
      ? '/companies/memos/' + room?.id
      : '',
  )
  const { data: gmemos } = useMessageGroupMemos(
    paid && room?.id && room.type === 'CompanyMessageGroupRoom'
      ? '/companies/group/memos/' + room?.id
      : '',
  )
  useEffect(() => {
    if (memos) setCurrentMemos(memos)

    if (gmemos) setCurrentMemos(gmemos)
  }, [memos, gmemos])

  const submit = async () => {
    if (paid && room?.id)
      try {
        if (room.type === 'CompanyMessageRoom') {
          const res = await createMessageMemo(room?.id, text)
          const m = toMessageMemo(res.data.memo)
          setCurrentMemos([m, ...currentMemos])
        } else {
          const res = await createMessageGroupMemo(room?.id, text)
          const m = toMessageGroupMemo(res.data.memo)
          setCurrentMemos([m, ...currentMemos])
        }
        setText('')
      } catch (err) {
        // through
      }
  }

  const deleteMemo = async (memo: MessageMemo) => {
    if (paid && room?.id && memo)
      try {
        if (room.type === 'CompanyMessageRoom') {
          await deleteMessageMemo(memo.id, memo.roomId)
          setCurrentMemos(currentMemos.filter((m) => m.id !== memo.id))
        } else {
          await deleteMessageGroupMemo(memo.id, memo.roomId)
          setCurrentMemos(currentMemos.filter((m) => m.id !== memo.id))
        }
      } catch (err) {
        // through
      }
  }

  return (
    <SubMenuSection className={className} isSubMenuOpen={isSubMenuOpen}>
      <TabList activeTab={activeTab} setTabType={setTabType} />
      {counters && (
        <>
          {activeTab === 'FeedbackTab' && room && (
            <FeedbackTab
              room={room}
              toggleSubMenu={toggleSubMenu}
              mutate={mutate}
              showProfile={showProfile}
            />
          )}
          {activeTab === 'Profile' && (
            <>
              {counters?.map((counter) => (
                <Profile
                  key={counter.id}
                  counter={counter}
                  showProfile={showProfile}
                />
              ))}
            </>
          )}
          {activeTab === 'Memo' && (
            <>
              <TooltipWrapper
                tooltipNode={'この機能は有料会員様のみ利用ができます'}
                direction={{ x: 'right', y: 'top' }}
                xValue={'0%'}
                yValue={'100%'}
                disabled={paid}
              >
                <MessageMemoFormDiv>
                  <TextareaAutosize
                    ref={textareaEl}
                    value={text}
                    minRows={4}
                    maxRows={4}
                    onChange={({ target: { value } }) => setText(value)}
                    disabled={!paid}
                    placeholder={'メモを入力'}
                  />
                  <Control>
                    <SubmitButton
                      onClick={submit}
                      disabled={text === '' || !paid}
                    >
                      メモを登録
                    </SubmitButton>
                  </Control>
                </MessageMemoFormDiv>
              </TooltipWrapper>

              <SubMenuElem title="過去登録済みのメモ">
                {currentMemos?.length === 0 && (
                  <Input placeholder="メモが登録されていません" disabled />
                )}
                {currentMemos?.map((memo) => (
                  <MessageMemoDiv key={memo.id}>
                    <TextareaAutosize
                      value={memo.content}
                      minRows={4}
                      maxRows={4}
                      disabled
                    />
                    <ControlDelete>
                      <DeleteDiv>
                        <TooltipWrapper
                          tooltipNode={'メモを削除'}
                          tooltipStyle={tooltipStyle}
                          direction={{ x: 'right', y: 'top' }}
                          xValue={'0%'}
                          yValue={'150%'}
                        >
                          <StyledTrashIcon
                            onClick={() => setDeleteMemoModal(memo)}
                          />
                        </TooltipWrapper>
                      </DeleteDiv>
                      <Date>{memo.createdAt.format('YYYY/MM/DD HH:mm')}</Date>
                    </ControlDelete>
                  </MessageMemoDiv>
                ))}
              </SubMenuElem>
            </>
          )}
        </>
      )}
      {deleteMemoModal && (
        <DeleteMemoModal
          deleteMemo={async () => {
            deleteMemo(deleteMemoModal)
          }}
          close={() => setDeleteMemoModal(undefined)}
        />
      )}
    </SubMenuSection>
  )
}
const SubMenuSection = styled.section<{ isSubMenuOpen: boolean }>`
  overflow-y: auto;

  transition: all 0.5s ease-in-out;
  ${({ isSubMenuOpen }) =>
    !isSubMenuOpen &&
    css`
      flex: 0 !important;
      width: 0;
      opacity: 0;
    `}

  @media screen and (max-width: 960px) {
    display: none;
  }
`

const Profile: VFC<{
  counter: Counter
  showProfile: (userId: number) => void
}> = ({
  counter: {
    id,
    iconSrc,
    companyName,
    name,
    position,
    role,
    feedbackScore,
    feedbackCount,
  },
  showProfile,
}) => {
  const { user: loginUser } = useLoginUser({
    dedupingInterval: IS_DEVELOPMENT ? undefined : 300000,
  })

  return (
    <SubMenuElem>
      <ProfileDiv>
        <UserIcon src={iconSrc} size="48px" />
        <p>{companyName}</p>
        <p>{position ? `${name} / ${position}` : name}</p>
        {shouldDisplayPaidPlanLabel([
          loginUser && loginUser.plan.role,
          role,
        ]) && <PaidUserLabel>有料会員</PaidUserLabel>}
        <FeedbackScore value={feedbackScore} feedbackCount={feedbackCount} />
        <ShowProfileButton onClick={() => showProfile(id)}>
          <ProfileIcon />
          <p>プロフィールを見る</p>
        </ShowProfileButton>
      </ProfileDiv>
    </SubMenuElem>
  )
}
const ProfileDiv = styled.div`
  background-color: ${_messagesbg};
  ${flexCenter};
  flex-direction: column;
  > p {
    font-weight: ${fontWeightBold};
  }
`

const SubMenuElem: VFC<{
  title?: string
  children?: ReactNode
}> = ({ title, children }) => {
  return (
    <SubMenuCard>
      {title && <SubMenuTitle>{title}</SubMenuTitle>}
      <SubMenuContent>{children}</SubMenuContent>
    </SubMenuCard>
  )
}
const Input = styled.input`
  ::placeholder {
    color: ${_menuicon};
    font-weight: ${fontWeightLight};
    display: flex;
    align-items: center;
    justify-content: center;
  }
  white-space: normal;
  width: 100%;
  padding: 8px 0px 8px 40px;
  min-height: 80px;
  background-color: ${truewhite};
`

const ShowProfileButton = styled(SmallButton)`
  ${flexCenter};
  margin: 10px;
  white-space: nowrap;
  border: 1px solid ${_profilegreen};
  color: ${_profilegreen};
  background: ${truewhite};
  svg {
    margin-right: 4px;
  }
  :hover {
    color: ${truewhite};
    background: ${_profilegreen};
  }
`
const DeleteDiv = styled.div`
  margin-left: -20px;
`
const tooltipStyle = css`
  color: ${truewhite};
  background: ${_messagebuttonbg};
`
const Card = styled.div`
  margin: 10px;
  display: flex;

  border-radius: 5px;
`
const SubMenuContent = styled(Card)`
  background: ${_mymessagebg};
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: 0px;

  input {
    margin-top: 8px;
  }
`

const SubMenuCard = styled(Card)`
  background-color: ${_messagesbg};
  font-weight: ${fontWeightSemiBold};
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 16px;
  ${SubMenuContent} {
    background-color: ${_messagesbg};
    font-weight: ${fontWeightLight};
  }
`

const SubMenuTitle = styled.p`
  font-size: 12px;
  margin: 0px;
`

const SubMenuStyle = styled(SubMenuComponent)``

const MessageMemoFormDiv = styled.div`
  border: 1px solid ${_messageformborder};
  border-radius: 5px;

  background-color: #ffffffc0;
  backdrop-filter: blur(50px) brightness(1.75);
  margin: 14px;
  padding: 14px 8px 8px 24px;
  text-align: left;

  textarea {
    width: 100%;
    resize: none;
    font-size: 14px;
    font-weight: ${fontWeightMedium};
    letter-spacing: 0.036em;
    line-height: 1.5;
    overflow-y: auto;
    cursor: auto;
  }
`

const MessageMemoDiv = styled.div`
  border: 1px solid ${_messageformborder};
  border-radius: 5px;

  background-color: #ffffffc0;
  backdrop-filter: blur(50px) brightness(1.75);
  margin-top: 8px;
  padding: 14px 8px 8px 24px;
  text-align: left;

  textarea {
    width: 100%;
    resize: none;
    font-size: 12px;
    font-weight: ${fontWeightMedium};
    letter-spacing: 0.036em;
    line-height: 1.5;
    overflow-y: auto;
    cursor: auto;
  }
`
const Control = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin: 16px 0 0;

  > div {
    display: flex;
    align-items: center;
    > * {
      margin: 0 16px;
    }
  }
`

const SubmitButton = styled.button`
  width: 83px;
  height: 30px;
  border-radius: 5px;
  color: ${_messagebuttonbg};
  background: ${truewhite};
  font-size: 14px;
  font-weight: ${fontWeightBold};
  text-align: center;
`

const ControlDelete = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 0;

  > div {
    display: flex;
    align-items: center;
    > * {
      margin: 0 16px;
    }
  }
`
const StyledTrashIcon = styled(TrashIcon)`
  cursor: pointer;
  :hover {
    path {
      fill: ${_messagebuttonbg};
    }
  }
`
const Date = styled.p`
  font-size: 12px;
  color: ${_filterbg};
`
